/**
 * OfferingsCatalogCockpit — Cockpit-language frame for /offerings.
 *
 * Wraps OfferingsCatalog with a Cockpit Masthead + KPI strip to guarantee
 * visible cockpit chrome. The interactive catalog admin content is preserved
 * verbatim — no business logic changed.
 */
import { useMemo } from "react";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip,
} from "@/components/cockpit/CockpitShell";
import { useCatalog } from "@/data/CatalogContext";
import OfferingsCatalog from "./OfferingsCatalog";

export default function OfferingsCatalogCockpit() {
  const { products, categories } = useCatalog();

  const stats = useMemo(() => {
    const total = products.length;
    const active = products.filter((p) => p.active).length;
    const variants = products.reduce((sum, p) => sum + (p.variants?.length || 0), 0);
    const activeVariants = products.reduce(
      (sum, p) => sum + (p.variants?.filter((v) => v.active).length || 0),
      0,
    );
    return { total, active, variants, activeVariants, categories: categories.length };
  }, [products, categories]);

  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow="Catalog · Offerings"
        title="Offerings Catalog"
        right={(
          <>
            <HeroMetric label="Active products" value={stats.active} hero />
            <HeroDivider />
            <HeroMetric label="Total" value={stats.total} />
            <HeroMetric label="Variants" value={stats.activeVariants} />
          </>
        )}
      />
      <CockpitKpiStrip
        items={[
          { label: "Categories", value: stats.categories },
          { label: "Total products", value: stats.total },
          { label: "Active", value: stats.active, tone: "good" },
          { label: "Variants", value: stats.variants },
          { label: "Active variants", value: stats.activeVariants, tone: "good" },
        ]}
      />
      <div className="cockpit-surface -mx-6">
        <div className="px-6">
          <OfferingsCatalog />
        </div>
      </div>
    </CockpitPage>
  );
}
